import React, { useState, useEffect } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from './components/ui/sonner';
import { AuthProvider, useAuth } from './lib/hooks/useAuth';
import { BackendIndicator } from './components/common/BackendIndicator';

// Component imports
import { LoginScreen } from './components/shared/LoginScreen';
import { ForgotPasswordScreen } from './components/shared/ForgotPasswordScreen';
import { SignUpScreen } from './components/shared/SignUpScreen';
import { PatientDashboard } from './components/patient/PatientDashboard';
import { DoctorDashboard } from './components/DoctorDashboard';
import { TherapistDashboard } from './components/TherapistDashboard';
import { CoordinatorDashboard } from './components/CoordinatorDashboard';
import { NurseDashboard } from './components/NurseDashboard';
import { PhysicianAssistantDashboard } from './components/physician_assistant/PhysicianAssistantDashboard';
import { ExercisePlayer } from './components/patient/ExercisePlayer';
import { VitalsManagement } from './components/patient/VitalsManagement';
import { ReportsAnalytics } from './components/ReportsAnalytics';
import { MessagingScreen } from './components/shared/MessagingScreen';
import { DoctorChatScreen } from './components/doctor/DoctorChatScreen';
import { CoordinatorChatScreen } from './components/coordinator/CoordinatorChatScreen';
import { TherapistChatScreen } from './components/therapist/TherapistChatScreen';
import { NurseChatScreen } from './components/nurse/NurseChatScreen';
import { PatientChatScreen } from './components/patient/PatientChatScreen';
import { PhysicianAssistantChatScreen } from './components/physician_assistant/PhysicianAssistantChatScreen';
import { AppointmentsScreen } from './components/shared/AppointmentsScreen';
import { CoordinatorAppointmentsScreen } from './components/coordinator/CoordinatorAppointmentsScreen';
import { VideoConsultation } from './components/shared/VideoConsultation';
import { ExerciseLibrary } from './components/ExerciseLibrary';
import { ProfileSettings } from './components/shared/ProfileSettings';
import { NotificationsCenter } from './components/shared/NotificationsCenter';
import { SymptomsManagement } from './components/patient/SymptomsManagement';
import { HomeTestSelection } from './components/HomeTestSelection';
import { SpirometryTest } from './components/SpirometryTest';
import { SixMinuteWalkTest } from './components/SixMinuteWalkTest';
import { LabReportUpload } from './components/LabReportUpload';
import { NurseVitalsChecklist } from './components/NurseVitalsChecklist';
import { NurseLabSampleCollection } from './components/NurseLabSampleCollection';
import { NursePatientStatusUpload } from './components/NursePatientStatusUpload';
import { NurseVitalsManagement } from './components/nurse/NurseVitalsManagement';
import { NurseTaskManagement } from './components/nurse/NurseTaskManagementWithCRUD_Fixed';
import { DischargeSummaryOnboarding } from './components/shared/DischargeSummaryOnboarding';
import { MedicationLog } from './components/patient/MedicationLog';
import { ExerciseLogManagement } from './components/patient/ExerciseLogManagement';
import { HomeTestLog } from './components/patient/HomeTestLog';
import { HomeTestsManagement } from './components/patient/HomeTestsManagement';
import { SpirometryTestWithCRUD } from './components/SpirometryTestWithCRUD';
import { SixMinuteWalkTestWithCRUD } from './components/SixMinuteWalkTestWithCRUD';
import { NurseTestsManagement } from './components/nurse/NurseTestsManagement';
import { Button } from './components/ui/button';
import { LogOut, User, Bell, Settings } from 'lucide-react';
import { toast } from 'sonner';

type UserRole = 'patient' | 'doctor' | 'physiotherapist' | 'coordinator' | 'nurse' | 'physician_assistant';
type AppView = 'login' | 'forgot-password' | 'signup' | 'dashboard' | 'exercise' | 'vitals' | 'reports' | 'messaging' | 'appointments' | 'video' | 'library' | 'settings' | 'notifications' | 'symptoms' | 'symptoms-manage' | 'hometest' | 'spirometry' | 'sixmwt' | 'labreport' | 'nurse-vitals' | 'nurse-lab-sample' | 'nurse-status' | 'nurse-vitals-manage' | 'nurse-tests-manage' | 'nurse-task-manage' | 'discharge-summary' | 'medication-log' | 'exercise-log' | 'hometest-log' | 'hometest-manage' | 'spirometry-test' | 'sixmwt-test';

// Create a QueryClient instance
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

// Inner component that can use useAuth hook
function AppContent() {
  const { user, loading: authLoading } = useAuth();
  const [appView, setAppView] = useState<AppView>('login');
  const [userRole, setUserRole] = useState<UserRole | null>(null);
  const [userName, setUserName] = useState<string>('');
  const [selectedPatient, setSelectedPatient] = useState<any>(null);
  const [nurseAssignedPatient, setNurseAssignedPatient] = useState<any>(null);

  // ✅ FIX: Check auth state on mount to restore session after page refresh
  useEffect(() => {
    if (!authLoading && user) {
      console.log('✅ App - Restoring session from AuthProvider:', user);
      
      // Map backend role to frontend role type
      const roleMap: Record<string, UserRole> = {
        PATIENT: 'patient',
        DOCTOR: 'doctor',
        THERAPIST: 'physiotherapist',
        COORDINATOR: 'coordinator',
        NURSE: 'nurse',
        PHYSICIAN_ASSISTANT: 'physician_assistant',
      };
      
      const frontendRole = roleMap[user.role] || 'patient';
      setUserRole(frontendRole);
      setUserName(user.full_name || user.email);
      setAppView('dashboard');
      
      console.log('✅ App - Session restored:', {
        frontendRole,
        userName: user.full_name || user.email,
        userId: user.id
      });
    } else if (!authLoading && !user) {
      console.log('⚠️ App - No user session found, showing login screen');
    }
  }, [user, authLoading]);

  const handleLogin = (role: UserRole, name: string) => {
    setUserRole(role);
    setUserName(name);
    setAppView('dashboard');
    toast.success(`Welcome back, ${name}!`, {
      description: `Logged in as ${role.charAt(0).toUpperCase() + role.slice(1)}`,
    });
  };

  const handleSignUpComplete = (role: UserRole, name: string) => {
    setUserRole(role);
    setUserName(name);
    setAppView('dashboard');
  };

  const handleNavigateToSignUp = () => {
    setAppView('signup');
  };

  const handleNavigateToForgotPassword = () => {
    setAppView('forgot-password');
  };

  const handleBackToLogin = () => {
    setAppView('login');
  };

  const handleLogout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');

    setUserRole(null);
    setUserName('');
    setAppView('login');
    toast.info('Logged out successfully');
  };

  const handleNavigate = (view: AppView) => {
    setAppView(view);
  };

  const getRoleDashboard = () => {
    switch (userRole) {
      case 'patient':
        return <PatientDashboard onNavigate={handleNavigate} onLogout={handleLogout} />;
      case 'doctor':
        return <DoctorDashboard onNavigate={handleNavigate} />;
      case 'physiotherapist':
        return <TherapistDashboard onNavigate={handleNavigate} onSelectPatient={setSelectedPatient} />;
      case 'coordinator':
        return <CoordinatorDashboard onNavigate={handleNavigate} />;
      case 'nurse':
        return <NurseDashboard onNavigate={handleNavigate} onSelectPatient={setNurseAssignedPatient} />;
      case 'physician_assistant':
        return <PhysicianAssistantDashboard onNavigate={handleNavigate} onSelectPatient={setSelectedPatient} />;
      default:
        return <PatientDashboard onNavigate={handleNavigate} onLogout={handleLogout} />;
    }
  };

  // Show loading state while checking auth
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // Wrap everything in content
  return (
    <>
      {/* Login Screen */}
      {appView === 'login' && (
        <LoginScreen 
          onLogin={handleLogin} 
          onNavigateToSignUp={handleNavigateToSignUp}
          onNavigateToForgotPassword={handleNavigateToForgotPassword}
        />
      )}

      {/* Forgot Password Screen */}
      {appView === 'forgot-password' && (
        <ForgotPasswordScreen onBackToLogin={handleBackToLogin} />
      )}

      {/* Sign Up Screen */}
      {appView === 'signup' && (
        <SignUpScreen onSignUpComplete={handleSignUpComplete} onBackToLogin={handleBackToLogin} />
      )}

        {/* Main Application */}
        {appView !== 'login' && appView !== 'signup' && (
          <div className="min-h-screen bg-background">
            {/* Header with User Info */}
            <div className="sticky top-0 z-50 bg-white border-b border-border shadow-sm">
              <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 py-2 sm:py-3">
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0 flex-shrink">
                    <h1 className="text-primary truncate text-lg sm:text-2xl">Pulmonology Spectrum by Healthkon</h1>
                    <p className="text-muted-foreground text-xs sm:text-sm hidden sm:block">Anytime - Anywhere - Healthcare</p>
                  </div>
                  
                  <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleNavigate('notifications')}
                      className="relative p-1 sm:p-2"
                    >
                      <Bell className="w-4 h-4 sm:w-5 sm:h-5" />
                      <span className="absolute top-0.5 right-0.5 sm:top-1 sm:right-1 w-2 h-2 bg-accent rounded-full" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleNavigate('settings')}
                      className="p-1 sm:p-2"
                    >
                      <Settings className="w-4 h-4 sm:w-5 sm:h-5" />
                    </Button>
                    <div className="hidden md:flex items-center gap-2 px-3 py-1.5 bg-primary/10 rounded-lg">
                      <User className="w-4 h-4 text-primary" />
                      <div className="text-right">
                        <p className="text-primary text-sm truncate max-w-[100px]">{userName}</p>
                        <p className="text-muted-foreground capitalize text-xs">{userRole}</p>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleLogout}
                      className="gap-1 sm:gap-2 px-2 sm:px-3"
                    >
                      <LogOut className="w-3 h-3 sm:w-4 sm:h-4" />
                      <span className="hidden sm:inline">Logout</span>
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            {/* Main Content */}
            <div>
              {appView === 'dashboard' && getRoleDashboard()}
              {appView === 'exercise' && <ExercisePlayer onBack={() => handleNavigate('dashboard')} />}
              {appView === 'reports' && <ReportsAnalytics onBack={() => handleNavigate('dashboard')} />}
              {appView === 'messaging' && (
                userRole === 'doctor' ? (
                  <DoctorChatScreen onBack={() => handleNavigate('dashboard')} />
                ) : userRole === 'coordinator' ? (
                  <CoordinatorChatScreen onBack={() => handleNavigate('dashboard')} />
                ) : userRole === 'physiotherapist' ? (
                  <TherapistChatScreen onBack={() => handleNavigate('dashboard')} />
                ) : userRole === 'nurse' ? (
                  <NurseChatScreen onBack={() => handleNavigate('dashboard')} />
                ) : userRole === 'patient' ? (
                  <PatientChatScreen onBack={() => handleNavigate('dashboard')} />
                ) : userRole === 'physician_assistant' ? (
                  <PhysicianAssistantChatScreen onBack={() => handleNavigate('dashboard')} />
                ) : (
                  <MessagingScreen onBack={() => handleNavigate('dashboard')} />
                )
              )}
              {appView === 'appointments' && (                
                  <AppointmentsScreen
                    userRole={userRole ?? "patient"}
                    onBack={() => handleNavigate("dashboard")}
                    onNavigate={handleNavigate}
                  />
              )}
              {appView === 'video' && <VideoConsultation onBack={() => handleNavigate('appointments')} />}
              {appView === 'library' && <ExerciseLibrary onBack={() => handleNavigate('dashboard')} onStartExercise={() => handleNavigate('exercise')} onNavigate={handleNavigate} onLogout={handleLogout} />}
              {appView === 'settings' && <ProfileSettings onBack={() => handleNavigate('dashboard')} userRole={userRole || 'patient'} />}
              {appView === 'notifications' && <NotificationsCenter onBack={() => handleNavigate('dashboard')} />}
              {appView === 'symptoms' && (
                <SymptomsManagement onNavigate={handleNavigate} onLogout={handleLogout} />
              )}
              {appView === 'symptoms-manage' && (
                <SymptomsManagement onNavigate={handleNavigate} onLogout={handleLogout} />
              )}
              {appView === 'vitals' && (
                <VitalsManagement onNavigate={handleNavigate} onLogout={handleLogout} />
              )}
              {appView === 'hometest' && <HomeTestSelection onBack={() => handleNavigate('dashboard')} onSelectTest={(test) => handleNavigate(test)} selectedPatient={(userRole === 'nurse' || userRole === 'physiotherapist') ? selectedPatient : undefined} onNavigate={handleNavigate} onLogout={handleLogout} />}
              {appView === 'spirometry' && (
                <SpirometryTest 
                  onBack={() => handleNavigate('hometest')} 
                  selectedPatient={(userRole === 'nurse' || userRole === 'physiotherapist') ? selectedPatient : undefined}
                  userRole={userRole}
                />
              )}
              {appView === 'sixmwt' && <SixMinuteWalkTest onBack={() => handleNavigate('hometest')} selectedPatient={(userRole === 'nurse' || userRole === 'physiotherapist') ? selectedPatient : undefined} />}
              {appView === 'labreport' && <LabReportUpload onBack={() => handleNavigate('dashboard')} onNavigate={handleNavigate} onLogout={handleLogout} />}
              {appView === 'nurse-vitals' && <NurseVitalsChecklist onBack={() => handleNavigate('dashboard')} selectedPatient={selectedPatient} />}
              {appView === 'nurse-lab-sample' && <NurseLabSampleCollection onBack={() => handleNavigate('dashboard')} selectedPatient={selectedPatient} />}
              {appView === 'nurse-status' && <NursePatientStatusUpload onBack={() => handleNavigate('dashboard')} selectedPatient={selectedPatient} />}
              {appView === 'discharge-summary' && <DischargeSummaryOnboarding onBack={() => handleNavigate('dashboard')} onNavigate={handleNavigate} onLogout={handleLogout} useOwnLayout={false} />}
              {appView === 'medication-log' && <MedicationLog onBack={() => handleNavigate('dashboard')} onNavigate={handleNavigate} onLogout={handleLogout} />}
              {appView === 'exercise-log' && <ExerciseLogManagement onBack={() => handleNavigate('dashboard')} onNavigate={handleNavigate} onLogout={handleLogout} />}
              {appView === 'hometest-log' && <HomeTestLog onBack={() => handleNavigate('dashboard')} onStartTest={(testType) => handleNavigate(testType)} onNavigate={handleNavigate} onLogout={handleLogout} />}
              {appView === 'hometest-manage' && (
                <div className="min-h-screen bg-[#D4F0F8] p-4">
                  <div className="max-w-4xl mx-auto">
                    <Button 
                      variant="outline" 
                      onClick={() => handleNavigate('dashboard')} 
                      className="mb-4"
                    >
                      ← Back to Dashboard
                    </Button>
                    <HomeTestsManagement onNavigate={handleNavigate} onLogout={handleLogout} />
                  </div>
                </div>
              )}
              {appView === 'spirometry-test' && (
                <SpirometryTestWithCRUD
                  onBack={() => handleNavigate('hometest-log')}
                  onNavigate={handleNavigate}
                  onLogout={handleLogout}
                />
              )}
              {appView === 'sixmwt-test' && (
                <SixMinuteWalkTestWithCRUD
                  onBack={() => handleNavigate('hometest-log')}
                  onNavigate={handleNavigate}
                  onLogout={handleLogout}
                />
              )}
              {appView === 'nurse-vitals-manage' && (
                <div className="min-h-screen bg-[#D4F0F8] p-4">
                  <div className="max-w-4xl mx-auto">
                    <Button 
                      variant="outline" 
                      onClick={() => handleNavigate('dashboard')} 
                      className="mb-4"
                    >
                      ← Back to Dashboard
                    </Button>
                    <NurseVitalsManagement patient={nurseAssignedPatient} />
                  </div>
                </div>
              )}
              {appView === 'nurse-tests-manage' && (
                <div className="min-h-screen bg-[#D4F0F8] p-4">
                  <div className="max-w-4xl mx-auto">
                    <Button 
                      variant="outline" 
                      onClick={() => handleNavigate('dashboard')} 
                      className="mb-4"
                    >
                      ← Back to Dashboard
                    </Button>
                    <NurseTestsManagement patient={nurseAssignedPatient} />
                  </div>
                </div>
              )}
              {appView === 'nurse-task-manage' && (
                <div className="min-h-screen bg-[#D4F0F8] p-4">
                  <div className="max-w-4xl mx-auto">
                    <Button 
                      variant="outline" 
                      onClick={() => handleNavigate('dashboard')} 
                      className="mb-4"
                    >
                      ← Back to Dashboard
                    </Button>
                    {nurseAssignedPatient ? (
                      <NurseTaskManagement 
                        patientId={nurseAssignedPatient.id} 
                        patientName={nurseAssignedPatient.full_name || nurseAssignedPatient.email || 'Patient'}
                      />
                    ) : (
                      <div className="text-center py-12">
                        <p className="text-muted-foreground">No patient assigned. Please go back to dashboard.</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Toast Notifications */}
            <Toaster position="top-center" richColors />

            {/* Backend Connection Indicator (for testing) */}
            <BackendIndicator />
          </div>
        )}

      {/* Toast Notifications - Keep at top level */}
      <Toaster position="top-center" richColors />

      {/* Backend Connection Indicator */}
      <BackendIndicator />
    </>
  );
}

// Main App component with providers
export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </QueryClientProvider>
  );
}